// DateControl.tsx
import React, { useEffect, useState } from 'react';
import { Control } from '../ControlRenderer';
import { formatDateValue, get, set } from '../utils/Utils'
import { TextField, Typography, FormControl, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType } from '@/components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import { format, isValid, parseISO } from "date-fns";
import ControlIcons from '../ControlIcons';

export interface DateControl extends Control {
  /**
   * A string used to render the current date value in readonly mode.
   */
  format?: string;

  /**
   * If true, the control will allow time selection in addition to date.
   */
  allowTime?: boolean;
}

interface DateControlProps {
  control: Control,
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const DateControl: React.FC<DateControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, warnings, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const handleFieldChange = (fieldName: string, value: string) => {
    if (value === '') {
      // Treat empty as null by default
      setSelectedValue('');
      set(data, fieldName, null);
      onDataChange(data);
    }
    else {
      setSelectedValue(value);
      set(data, fieldName, value);
      const parsedDate = new Date(value);
      if (parsedDate && isNaN(parsedDate.getTime())) {
        setSelectedDate(undefined);
      }
      else {
        setSelectedDate(parsedDate);
      }
      onDataChange(data);
    }
  };

  const textStyle: React.CSSProperties = {
  };

  const [selectedValue, setSelectedValue] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>();

  const [isFocused, setIsFocused] = useState(false);

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;
  const warningMessage = warnings && control.dataKey ? warnings[control.dataKey] : undefined;

  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage || !!warningMessage;

  const dateControl = control as DateControl;
  const allowTime = dateControl.allowTime ?? false;

  useEffect(() => {
    const key = control.dataKey;
    if (!key) return;
    const rawValue = get(data, key);
    if (!rawValue) {
      setSelectedValue('');
      setSelectedDate(undefined);
    } else {
      const parsedDate = parseISO(rawValue);
      if (!isValid(parsedDate)) {
        setSelectedValue('');
        setSelectedDate(undefined);
      } else {
        setSelectedDate(parsedDate);
        setSelectedValue(allowTime ? format(parsedDate, "yyyy-MM-dd'T'HH:mm") : format(parsedDate, "yyyy-MM-dd"));
      }
    }
  }, [data, control.dataKey, allowTime]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  let currentMode = control.mode ? control.mode : mode;

  const dateFormat = dateControl.format ?? "MM/dd/yyyy";
  const displayValue = formatDateValue(selectedDate, dateFormat) ?? '';
  const inputValue = currentMode === 'view' ? displayValue : selectedValue;

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {displayValue}
      </Typography>
    )
  }

  // Conditionally render type="text" when in read-only mode and selectedValue is empty
  const inputType = currentMode === 'view' ? 'text' : (allowTime ? 'datetime-local' : 'date')

  if (currentMode === 'view') {
    textStyle.backgroundColor = '#ebebeb';
  }

  if (control.minWidth) {
    textStyle.minWidth = control.minWidth;
  }

  return (
    <Tooltip
      title={selectedDate && !isFocused ? format(selectedDate, 'M/d/yyyy h:mm:ss a') : ''}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -10], // move 8px closer to the item
            },
          },
        ],
      }}
      arrow>
      {hasControlIcons ? (
        <FormControl variant="standard" fullWidth error={!!errorMessage}><TextField
          type={inputType}
          variant="standard"
          className={`eform-date eform-text-${theme.palette.mode}`}
          value={inputValue}
          error={!!errorMessage}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); validateData(); }}
          inputProps={{ readOnly: currentMode === 'view' }}
          style={textStyle}
        />
        <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} offsetRight={15} />
        </FormControl>
      ) : (
        <TextField
          type={inputType}
          variant="standard"
          className={`eform-date eform-text-${theme.palette.mode}`}
          value={inputValue}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); validateData(); }}
          inputProps={{ readOnly: currentMode === 'view' }}
          style={textStyle}
        />
      )}
    </Tooltip>
  );
};

export default DateControl;
